/**
 * Global workspace registry — single source of truth for cross-module
 * Import/Export, Reports, Checklist, and Settings hubs.
 *
 * Feature entries deep-link into existing module screens via `to` + optional
 * `tab` / `sub`. RBAC is evaluated per feature (module + screen), not per hub.
 */

export const WORKSPACE_KEYS = ["reports", "import_export", "checklist", "settings"];

export const WORKSPACE_DEFS = [
  {
    key: "reports",
    label: "Reports",
    description: "Run and export reports across Employee, Attendance, Payroll, and Helpdesk.",
    path: "/admin/workspace/reports",
    icon: "FileBarChart",
  },
  {
    key: "import_export",
    label: "Import / Export",
    description: "Bulk import and export tools for employee, attendance, and payroll data.",
    path: "/admin/workspace/import_export",
    icon: "ArrowLeftRight",
  },
  {
    key: "checklist",
    label: "Checklist",
    description: "Operational checklists for attendance processing and payroll tasks.",
    path: "/admin/workspace/checklist",
    icon: "ListChecks",
  },
  {
    key: "settings",
    label: "Settings",
    description: "Module and organisation settings in one place.",
    path: "/admin/workspace/settings",
    icon: "Settings",
  },
];

/** Module sections shown inside each workspace (order preserved). */
export const WORKSPACE_MODULES = [
  { key: "employee", label: "Employee", path: "/admin/employee" },
  { key: "attendance", label: "Attendance", path: "/admin/attendance" },
  { key: "payroll", label: "Payroll", path: "/admin/payroll" },
  { key: "statutory", label: "Statutory", path: "/admin/statutory-reports" },
  { key: "hiring", label: "Hiring", path: "/admin/hiring" },
  { key: "assets", label: "Assets", path: "/admin/assets" },
  { key: "helpdesk", label: "Helpdesk", path: "/admin/helpdesk" },
  { key: "configuration", label: "Configuration", path: "/admin/configuration" },
  { key: "user_roles", label: "User role management", path: "/admin/global-settings" },
];

/**
 * @typedef {object} WorkspaceFeature
 * @property {string} id
 * @property {'reports'|'import_export'|'checklist'|'settings'} workspace
 * @property {string} moduleKey — WORKSPACE_MODULES.key
 * @property {string} label
 * @property {string} [description]
 * @property {'card'|'link'} kind
 * @property {string} to — base path
 * @property {string} [tab] — ?tab=
 * @property {string} [sub] — optional secondary key for future deep links
 * @property {string} rbacModule
 * @property {string} rbacScreen
 * @property {string} [rbacAction]
 */

/** @type {WorkspaceFeature[]} */
export const WORKSPACE_FEATURES = [
  // ——— Reports (Employee — all module report types as links) ———
  {
    id: "emp-report-employees",
    workspace: "reports",
    moduleKey: "employee",
    label: "Employee Report",
    kind: "link",
    to: "/admin/employee",
    tab: "reports",
    sub: "employees",
    rbacModule: "employee",
    rbacScreen: "employee_reports",
  },
  {
    id: "emp-report-age",
    workspace: "reports",
    moduleKey: "employee",
    label: "Age Report",
    kind: "link",
    to: "/admin/employee",
    tab: "reports",
    sub: "age-report",
    rbacModule: "employee",
    rbacScreen: "employee_reports",
  },
  {
    id: "emp-report-service",
    workspace: "reports",
    moduleKey: "employee",
    label: "Service Report",
    kind: "link",
    to: "/admin/employee",
    tab: "reports",
    sub: "service-report",
    rbacModule: "employee",
    rbacScreen: "employee_reports",
  },
  {
    id: "emp-report-salary",
    workspace: "reports",
    moduleKey: "employee",
    label: "Salary Report",
    kind: "link",
    to: "/admin/employee",
    tab: "reports",
    sub: "salary-report",
    rbacModule: "employee",
    rbacScreen: "employee_reports",
  },
  {
    id: "emp-report-confirmation",
    workspace: "reports",
    moduleKey: "employee",
    label: "Confirmation Report",
    kind: "link",
    to: "/admin/employee",
    tab: "reports",
    sub: "confirmation",
    rbacModule: "employee",
    rbacScreen: "employee_reports",
  },
  {
    id: "emp-report-transfers",
    workspace: "reports",
    moduleKey: "employee",
    label: "Transfers Report",
    kind: "link",
    to: "/admin/employee",
    tab: "reports",
    sub: "transfers",
    rbacModule: "employee",
    rbacScreen: "employee_reports",
  },
  {
    id: "emp-report-resignations",
    workspace: "reports",
    moduleKey: "employee",
    label: "Resignations Report",
    kind: "link",
    to: "/admin/employee",
    tab: "reports",
    sub: "resignations",
    rbacModule: "employee",
    rbacScreen: "employee_reports",
  },
  // ——— Reports (Attendance) ———
  {
    id: "att-report-checks",
    workspace: "reports",
    moduleKey: "attendance",
    label: "Attendance Checks",
    kind: "link",
    to: "/admin/attendance",
    tab: "reports",
    sub: "attendance-checks",
    rbacModule: "attendance",
    rbacScreen: "attendance_history",
  },
  {
    id: "att-report-daily",
    workspace: "reports",
    moduleKey: "attendance",
    label: "Daily Attendance Report",
    kind: "link",
    to: "/admin/attendance",
    tab: "reports",
    sub: "daily-attendance",
    rbacModule: "attendance",
    rbacScreen: "attendance_history",
  },
  {
    id: "att-report-who-in",
    workspace: "reports",
    moduleKey: "attendance",
    label: "Who are in",
    kind: "link",
    to: "/admin/attendance",
    tab: "reports",
    sub: "who-are-in",
    rbacModule: "attendance",
    rbacScreen: "attendance_history",
  },
  {
    id: "att-report-monthly",
    workspace: "reports",
    moduleKey: "attendance",
    label: "Monthly Attendance Report",
    kind: "link",
    to: "/admin/attendance",
    tab: "reports",
    sub: "monthly-attendance",
    rbacModule: "attendance",
    rbacScreen: "attendance_history",
  },
  {
    id: "att-report-leave",
    workspace: "reports",
    moduleKey: "attendance",
    label: "Leave Balance Report",
    kind: "link",
    to: "/admin/attendance",
    tab: "reports",
    sub: "leave-report",
    rbacModule: "attendance",
    rbacScreen: "attendance_history",
  },
  {
    id: "att-report-overtime",
    workspace: "reports",
    moduleKey: "attendance",
    label: "Overtime Report",
    kind: "link",
    to: "/admin/attendance",
    tab: "reports",
    sub: "overtime-report",
    rbacModule: "attendance",
    rbacScreen: "attendance_history",
  },
  {
    id: "att-report-lop",
    workspace: "reports",
    moduleKey: "attendance",
    label: "LOP Reversal Report",
    kind: "link",
    to: "/admin/attendance",
    tab: "reports",
    sub: "lop-reversal-report",
    rbacModule: "attendance",
    rbacScreen: "attendance_history",
  },
  {
    id: "att-report-comp-off",
    workspace: "reports",
    moduleKey: "attendance",
    label: "Comp Off Ledger Report",
    kind: "link",
    to: "/admin/attendance",
    tab: "reports",
    sub: "comp-off-ledger",
    rbacModule: "attendance",
    rbacScreen: "comp_off_ledger_report",
  },
  {
    id: "statutory-reports-hub",
    workspace: "reports",
    moduleKey: "statutory",
    label: "Statutory Reports",
    description: "Karnataka Shops & Establishment, Factories, and Central PF/ESI compliance registers.",
    kind: "link",
    to: "/admin/statutory-reports",
    rbacModule: "statutory_reports",
    rbacScreen: "statutory_reports_hub",
  },
  // ——— Reports (Payroll) ———
  {
    id: "pay-salary-revision-report",
    workspace: "reports",
    moduleKey: "payroll",
    label: "Salary Revision Report",
    kind: "link",
    to: "/admin/payroll",
    tab: "reports",
    sub: "salary-revision",
    rbacModule: "payroll",
    rbacScreen: "salary_revision_report",
  },
  {
    id: "pay-unpaid-salary-report",
    workspace: "reports",
    moduleKey: "payroll",
    label: "Unpaid Salary",
    kind: "link",
    to: "/admin/payroll",
    tab: "reports",
    sub: "unpaid-salary",
    rbacModule: "payroll",
    rbacScreen: "unpaid_salary_report",
  },
  {
    id: "pay-form16",
    workspace: "reports",
    moduleKey: "payroll",
    label: "Form-16",
    kind: "link",
    to: "/admin/payroll",
    tab: "reports",
    sub: "form-16",
    rbacModule: "payroll",
    rbacScreen: "payslips",
  },
  {
    id: "hd-reports",
    workspace: "reports",
    moduleKey: "helpdesk",
    label: "Helpdesk Reports",
    kind: "link",
    to: "/admin/helpdesk",
    tab: "reports",
    rbacModule: "helpdesk",
    rbacScreen: "helpdesk_config",
  },
  {
    id: "ast-reports",
    workspace: "reports",
    moduleKey: "assets",
    label: "Asset Reports",
    kind: "link",
    to: "/admin/assets",
    tab: "reports",
    rbacModule: "assets",
    rbacScreen: "asset_reports",
  },
  {
    id: "ast-report-register",
    workspace: "reports",
    moduleKey: "assets",
    label: "Asset Register",
    kind: "link",
    to: "/admin/assets",
    tab: "reports",
    sub: "register",
    rbacModule: "assets",
    rbacScreen: "asset_reports",
  },
  {
    id: "ast-report-employee",
    workspace: "reports",
    moduleKey: "assets",
    label: "Employee-wise Assets",
    kind: "link",
    to: "/admin/assets",
    tab: "reports",
    sub: "employee_wise",
    rbacModule: "assets",
    rbacScreen: "asset_reports",
  },
  {
    id: "ast-report-stock",
    workspace: "reports",
    moduleKey: "assets",
    label: "Stock Report",
    kind: "link",
    to: "/admin/assets",
    tab: "reports",
    sub: "stock",
    rbacModule: "assets",
    rbacScreen: "asset_reports",
  },
  {
    id: "ast-report-movement",
    workspace: "reports",
    moduleKey: "assets",
    label: "Asset Movement",
    kind: "link",
    to: "/admin/assets",
    tab: "reports",
    sub: "movement",
    rbacModule: "assets",
    rbacScreen: "asset_reports",
  },
  {
    id: "ast-report-warranty",
    workspace: "reports",
    moduleKey: "assets",
    label: "Warranty Expiry",
    kind: "link",
    to: "/admin/assets",
    tab: "reports",
    sub: "warranty_expiry",
    rbacModule: "assets",
    rbacScreen: "asset_reports",
  },
  {
    id: "ast-report-fnf",
    workspace: "reports",
    moduleKey: "assets",
    label: "F&F Recovery",
    kind: "link",
    to: "/admin/assets",
    tab: "reports",
    sub: "fnf_recovery",
    rbacModule: "assets",
    rbacScreen: "asset_reports",
  },
  {
    id: "ast-report-pending-requests",
    workspace: "reports",
    moduleKey: "assets",
    label: "Pending Asset Requests",
    kind: "link",
    to: "/admin/assets",
    tab: "reports",
    sub: "pending_requests",
    rbacModule: "assets",
    rbacScreen: "asset_reports",
  },

  // ——— Import / Export ———
  {
    id: "emp-import",
    workspace: "import_export",
    moduleKey: "employee",
    label: "Import Employee Details",
    description: "Upload organisation and section data from Excel templates.",
    kind: "card",
    to: "/admin/employee",
    tab: "import",
    rbacModule: "employee",
    rbacScreen: "employee_import",
  },
  {
    id: "emp-export",
    workspace: "import_export",
    moduleKey: "employee",
    label: "Export Employee Data",
    description: "Download employee sections or full data exports.",
    kind: "card",
    to: "/admin/employee",
    tab: "export",
    rbacModule: "employee",
    rbacScreen: "employee_export",
  },
  {
    id: "emp-bulk-exit",
    workspace: "import_export",
    moduleKey: "employee",
    label: "Bulk Employees Exit",
    description:
      "Import Relieved / Terminated exit details for existing employees from Excel.",
    kind: "card",
    to: "/admin/employee",
    tab: "bulk-exit",
    rbacModule: "employee",
    rbacScreen: "employee_bulk_exit",
  },
  {
    id: "att-import",
    workspace: "import_export",
    moduleKey: "attendance",
    label: "Bulk Import Attendance",
    description: "Upload biometric or spreadsheet attendance punches.",
    kind: "card",
    to: "/admin/attendance",
    tab: "import-attendance",
    rbacModule: "attendance",
    rbacScreen: "attendance_import",
  },
  {
    id: "att-import-leave",
    workspace: "import_export",
    moduleKey: "attendance",
    label: "Bulk Leave Upload",
    kind: "link",
    to: "/admin/attendance",
    tab: "import-leave",
    rbacModule: "attendance",
    rbacScreen: "leave_requests",
  },
  {
    id: "att-import-comp-off",
    workspace: "import_export",
    moduleKey: "attendance",
    label: "Comp Off Opening Balance",
    description: "Import Comp Off lots earned before PaceHRM go-live (worked date, days, expiry).",
    kind: "link",
    to: "/admin/attendance",
    tab: "import-comp-off-ob",
    rbacModule: "attendance",
    rbacScreen: "comp_off_opening_import",
  },
  {
    id: "att-import-lopr",
    workspace: "import_export",
    moduleKey: "attendance",
    label: "Bulk LOP Reversal",
    description:
      "Import LOP reversals (employee, LOP date, new status, payroll month) for locked attendance months.",
    kind: "link",
    to: "/admin/attendance",
    tab: "import-lopr",
    rbacModule: "attendance",
    rbacScreen: "attendance_lop_reversal_import",
  },
  {
    id: "att-export",
    workspace: "import_export",
    moduleKey: "attendance",
    label: "Export Attendance",
    kind: "link",
    to: "/admin/attendance",
    tab: "export",
    rbacModule: "attendance",
    rbacScreen: "attendance_history",
    rbacAction: "export",
  },
  {
    id: "att-import-checks",
    workspace: "import_export",
    moduleKey: "attendance",
    label: "Import Checks",
    description:
      "Upload punch checks (IN/OUT) from Excel. Validates sequence; append or replace day.",
    kind: "link",
    rbacModule: "attendance",
    rbacScreen: "attendance_import_checks",
  },
  {
    id: "ast-import",
    workspace: "import_export",
    moduleKey: "assets",
    label: "Import Assets",
    description: "Upload asset master, stock, assignments, and adjustments.",
    kind: "card",
    to: "/admin/assets",
    tab: "import",
    rbacModule: "assets",
    rbacScreen: "asset_import",
  },
  {
    id: "ast-export",
    workspace: "import_export",
    moduleKey: "assets",
    label: "Export Assets",
    description: "Download asset datasets and templates.",
    kind: "card",
    to: "/admin/assets",
    tab: "export",
    rbacModule: "assets",
    rbacScreen: "asset_import",
    rbacAction: "export",
  },
  {
    id: "pay-import-revision",
    workspace: "import_export",
    moduleKey: "payroll",
    label: "Import Salary Revision",
    description: "Upload salary revision sheets for processing.",
    kind: "card",
    to: "/admin/payroll",
    tab: "dashboard",
    sub: "import_salary_revision",
    rbacModule: "payroll",
    rbacScreen: "salary_revision_import",
  },
  {
    id: "pay-import-structure-components",
    workspace: "import_export",
    moduleKey: "payroll",
    label: "Import Structure Components",
    description:
      "Upload Manual fixed component amounts for a salary structure. Profile CTC / Gross stays the source of truth.",
    kind: "card",
    to: "/admin/payroll",
    tab: "dashboard",
    sub: "import_structure_components",
    rbacModule: "payroll",
    rbacScreen: "salary_structure_component_import",
  },
  {
    id: "cfg-import-masters",
    workspace: "import_export",
    moduleKey: "configuration",
    label: "Import Masters",
    description:
      "Bulk upload Branch, Department, Designation, Sub Department, Grade, Category, and Reasons from Excel templates.",
    kind: "card",
    to: "/admin/configuration",
    tab: "masters",
    rbacModule: "configuration",
    rbacScreen: "masters_import",
  },
  {
    id: "roles-export",
    workspace: "import_export",
    moduleKey: "user_roles",
    label: "Export Roles & permissions",
    description: "Download custom and system roles with screen permission matrix as Excel.",
    kind: "card",
    to: "/admin/workspace/import_export",
    rbacModule: "user_roles",
    rbacScreen: "roles_export",
    rbacAction: "export",
  },
  {
    id: "roles-import",
    workspace: "import_export",
    moduleKey: "user_roles",
    label: "Import Roles & permissions",
    description: "Upload roles Excel to create or update custom role screen permissions. System roles are skipped.",
    kind: "card",
    to: "/admin/workspace/import_export",
    rbacModule: "user_roles",
    rbacScreen: "roles_import",
    rbacAction: "import",
  },

  // ——— Checklist ———
  {
    id: "att-checklist",
    workspace: "checklist",
    moduleKey: "attendance",
    label: "Attendance Checklist",
    description: "Track month-end attendance processing tasks.",
    kind: "card",
    to: "/admin/attendance",
    tab: "checklist",
    rbacModule: "attendance",
    rbacScreen: "attendance_checklist",
  },
  {
    id: "pay-checklist",
    workspace: "checklist",
    moduleKey: "payroll",
    label: "Payroll Checklist Tasks",
    description: "Complete payroll run checklist items before finalise.",
    kind: "card",
    to: "/admin/payroll",
    tab: "checklist_tasks",
    rbacModule: "payroll",
    rbacScreen: "payroll_checklist",
  },

  // ——— Settings (one link per in-module settings sub-tab) ———
  {
    id: "emp-settings-general",
    workspace: "settings",
    moduleKey: "employee",
    label: "General Settings",
    description: "ESS and general employee module preferences.",
    kind: "card",
    to: "/admin/employee",
    tab: "settings",
    sub: "generalSettings",
    rbacModule: "employee",
    rbacScreen: "employee_settings",
  },
  {
    id: "emp-settings-code",
    workspace: "settings",
    moduleKey: "employee",
    label: "Employee Code",
    description: "Employee code structure and generation rules.",
    kind: "card",
    to: "/admin/employee",
    tab: "settings",
    sub: "employeeCode",
    rbacModule: "employee",
    rbacScreen: "employee_settings",
  },
  {
    id: "emp-settings-policies",
    workspace: "settings",
    moduleKey: "employee",
    label: "Policies",
    description: "Confirmation, transfer, separation, and onboarding policies.",
    kind: "card",
    to: "/admin/employee",
    tab: "settings",
    sub: "policies",
    rbacModule: "employee",
    rbacScreen: "employee_settings",
  },
  {
    id: "emp-settings-letters",
    workspace: "settings",
    moduleKey: "employee",
    label: "Letters Management",
    description: "Letter templates and generation.",
    kind: "card",
    to: "/admin/employee",
    tab: "settings",
    sub: "lettersManagement",
    rbacModule: "employee",
    rbacScreen: "letters_management",
  },
  {
    id: "att-settings-general",
    workspace: "settings",
    moduleKey: "attendance",
    label: "General Settings",
    description: "Attendance general preferences.",
    kind: "card",
    to: "/admin/attendance",
    tab: "settings",
    sub: "generalSettings",
    rbacModule: "attendance",
    rbacScreen: "attendance_general_settings",
  },
  {
    id: "att-settings-policies",
    workspace: "settings",
    moduleKey: "attendance",
    label: "Policies",
    description: "Weekly off, holiday, leave, shift, and attendance policies.",
    kind: "card",
    to: "/admin/attendance",
    tab: "settings",
    sub: "policies",
    rbacModule: "attendance",
    rbacScreen: "attendance_policy_settings",
    // Role editor grants each policy tab separately; any one opens the card.
    rbacAnyScreens: [
      "attendance_weekly_off_settings",
      "attendance_holiday_settings",
      "attendance_leave_settings",
      "attendance_shift_settings",
    ],
  },
  {
    id: "att-settings-codes",
    workspace: "settings",
    moduleKey: "attendance",
    label: "Codes and devices",
    description: "Attendance codes and biometric devices.",
    kind: "card",
    to: "/admin/attendance",
    tab: "settings",
    sub: "codesAndDevices",
    rbacModule: "attendance",
    rbacScreen: "attendance_code_mapping",
    rbacAnyScreens: ["attendance_devices"],
  },
  {
    id: "pay-settings-general",
    workspace: "settings",
    moduleKey: "payroll",
    label: "General Settings",
    description: "Payroll cycle, min wages, and general preferences.",
    kind: "card",
    to: "/admin/payroll",
    tab: "settings",
    sub: "general",
    rbacModule: "payroll",
    rbacScreen: "payroll_settings",
  },
  {
    id: "pay-settings-salary-structures",
    workspace: "settings",
    moduleKey: "payroll",
    label: "Salary Structures",
    description: "Salary structure templates and component library.",
    kind: "card",
    to: "/admin/payroll",
    tab: "settings",
    sub: "salary_structures",
    rbacModule: "payroll",
    rbacScreen: "salary_structure",
  },
  {
    id: "pay-settings-statutory",
    workspace: "settings",
    moduleKey: "payroll",
    label: "Statutory Rates and Reference Heads",
    description: "PF, ESI, PT rates and TDS reference heads.",
    kind: "card",
    to: "/admin/payroll",
    tab: "settings",
    sub: "statutory_rates_heads",
    rbacModule: "payroll",
    rbacScreen: "statutory_rates",
  },
  {
    id: "pay-settings-declaration",
    workspace: "settings",
    moduleKey: "payroll",
    label: "Declaration",
    description: "Investment declaration windows and settings.",
    kind: "card",
    to: "/admin/payroll",
    tab: "settings",
    sub: "declaration",
    rbacModule: "payroll",
    rbacScreen: "payroll_settings",
  },
  {
    id: "pay-settings-setup",
    workspace: "settings",
    moduleKey: "payroll",
    label: "Payroll Setup",
    description: "Payroll run steps and setup configuration.",
    kind: "card",
    to: "/admin/payroll",
    tab: "settings",
    sub: "payroll_setup",
    rbacModule: "payroll",
    rbacScreen: "payroll_settings",
  },

  // ——— Settings (Configuration / org tools) ———
  {
    id: "cfg-settings-activity-log",
    workspace: "settings",
    moduleKey: "configuration",
    label: "Activity Log Monitor",
    description:
      "Company-wide audit log — hot 30 days, compressed archives up to 1 year.",
    kind: "card",
    to: "/admin/activity-log-monitor",
    rbacModule: "workspaces",
    rbacScreen: "activity_log_monitor",
  },
  {
    id: "cfg-settings-notification-emails",
    workspace: "settings",
    moduleKey: "configuration",
    label: "Notification emails",
    description:
      "Product and custom email alerts — enable, audience, subject, and HTML templates.",
    kind: "card",
    to: "/admin/notification-emails",
    rbacModule: "workspaces",
    rbacScreen: "notification_emails",
  },

  // ——— Settings (Hiring) ———
  {
    id: "hir-settings-policies",
    workspace: "settings",
    moduleKey: "hiring",
    label: "Hiring policies",
    description: "Job request approval workflow and applicability.",
    kind: "card",
    to: "/admin/hiring",
    tab: "settings",
    sub: "policies",
    rbacModule: "hiring",
    rbacScreen: "hiring_settings",
  },
  {
    id: "hir-settings-ids",
    workspace: "settings",
    moduleKey: "hiring",
    label: "ID formats",
    description: "Hiring and portal ID prefixes, digits, and starting numbers.",
    kind: "card",
    to: "/admin/hiring",
    tab: "settings",
    sub: "ids",
    rbacModule: "hiring",
    rbacScreen: "hiring_settings",
  },
  {
    id: "hir-settings-mandatory",
    workspace: "settings",
    moduleKey: "hiring",
    label: "Mandatory profile fields",
    description: "Required candidate and employee profile fields for hiring.",
    kind: "card",
    to: "/admin/hiring",
    tab: "settings",
    sub: "mandatory",
    rbacModule: "hiring",
    rbacScreen: "hiring_settings",
  },
  // {
  //   id: "hir-settings-portal",
  //   workspace: "settings",
  //   moduleKey: "hiring",
  //   label: "Portal email & alerts",
  //   description: "Recruitment email templates, SMTP, and notification rules (Notification emails).",
  //   kind: "card",
  //   to: "/admin/notification-emails",
  //   rbacModule: "workspaces",
  //   rbacScreen: "notification_emails",
  // },
  {
    id: "ast-settings",
    workspace: "settings",
    moduleKey: "assets",
    label: "Asset Settings",
    description: "Categories, types, numbering, assignment and recovery rules.",
    kind: "card",
    to: "/admin/assets",
    tab: "settings",
    rbacModule: "assets",
    rbacScreen: "asset_settings",
  },

];

export function getWorkspaceDef(key) {
  return WORKSPACE_DEFS.find((w) => w.key === key) || null;
}

/** True when the user holds the feature's screen, or any alternate screen in rbacAnyScreens. */
export function isFeatureGranted(feature, hasScreenAccess) {
  const action = feature.rbacAction || "view";
  if (hasScreenAccess(feature.rbacModule, feature.rbacScreen, action)) return true;
  return (feature.rbacAnyScreens || []).some((screen) =>
    hasScreenAccess(feature.rbacModule, screen, action)
  );
}

export function getFeaturesForWorkspace(workspaceKey) {
  return WORKSPACE_FEATURES.filter((f) => f.workspace === workspaceKey);
}

/**
 * Section children for secondary-nav workspace flyouts.
 * Plain module names only (Employee, Attendance, …) — not "Employee reports".
 */
export function getWorkspaceSectionChildren(_workspaceKey, features = []) {
  const byModule = new Set(
    (features || []).map((f) => f.moduleKey).filter(Boolean)
  );
  return WORKSPACE_MODULES.filter((m) => byModule.has(m.key)).map((m) => ({
    key: m.key,
    label: m.label,
  }));
}

export function buildFeatureHref(feature) {
  if (!feature?.to) return "/admin/home";
  const params = new URLSearchParams();
  if (feature.tab) params.set("tab", feature.tab);
  if (feature.sub) params.set("sub", feature.sub);
  const q = params.toString();
  return q ? `${feature.to}?${q}` : feature.to;
}

/**
 * Match current location to a workspace feature (deep-links into modules).
 * Prefer tab+sub matches so /admin/payroll?tab=reports stays under Reports chrome.
 */
export function findWorkspaceFeatureForLocation(pathname, search = "") {
  const path = String(pathname || "");
  if (!path) return null;
  const params = new URLSearchParams(
    String(search || "").startsWith("?")
      ? String(search).slice(1)
      : String(search || "")
  );
  const tab = params.get("tab") || "";
  const sub = params.get("sub") || "";

  const candidates = WORKSPACE_FEATURES.filter((f) => {
    if (!f?.to) return false;
    if (path !== f.to && !path.startsWith(`${f.to}/`)) return false;
    if (f.tab) {
      if (f.tab !== tab) return false;
    } else if (tab) {
      // Bare module path features must not steal tabbed deep-links
      return false;
    }
    if (f.sub && f.sub !== sub) return false;
    return true;
  });

  if (!candidates.length) return null;
  candidates.sort((a, b) => {
    const score = (f) => (f.sub ? 4 : 0) + (f.tab ? 2 : 0);
    return score(b) - score(a);
  });
  return candidates[0];
}

/** Active workspace hub key for secondary nav (hub path or deep-linked feature). */
export function getActiveWorkspaceKey(pathname, search = "") {
  const m = String(pathname || "").match(/\/admin\/workspace\/([^/?#]+)/);
  if (m?.[1]) return m[1];
  return findWorkspaceFeatureForLocation(pathname, search)?.workspace || null;
}

/** Core-only children for Admin module accordion (no Import/Reports/Settings/Checklist). */
export const MODULE_CORE_CHILDREN = {
  configuration: [
    { key: "enterprise", label: "Enterprise" },
    { key: "masters", label: "Masters" },
  ],
  employee: [
    { key: "dashboard", label: "Dashboard" },
    { key: "employees", label: "Employee" },
    { key: "onboard", label: "Onboard Employee" },
  ],
  attendance: [
    { key: "dashboard", label: "Dashboard" },
    { key: "process", label: "Attendance Overview" },
  ],
  payroll: [
    { key: "dashboard", label: "Dashboard" },
    { key: "run_payroll", label: "Payroll Overview" },
    { key: "statutory_filing", label: "Statutory Filing" },
  ],
  helpdesk: [
    { key: "dashboard", label: "Dashboard" },
    { key: "tickets", label: "Tickets" },
    { key: "catalog", label: "Catalog" },
  ],
  hiring: [
    { key: "dashboard", label: "Dashboard" },
    { key: "jobs", label: "Jobs" },
    { key: "candidates", label: "Candidates" },
    { key: "ats", label: "ATS" },
    { key: "referrals", label: "Referrals" },
  ],
  assets: [
    { key: "dashboard", label: "Dashboard" },
    { key: "register", label: "Asset Register" },
    { key: "requests", label: "Requests" },
    { key: "operations", label: "Operations" },
  ],
};
